import React from 'react';
import ReactSelect, { Props as ReactSelectProps, components } from 'react-select';
import { ErrorMessage } from '@hookform/error-message';
import { FieldErrors } from 'react-hook-form';
import chevRght from '../../assets/images/chev-right.svg'
import { Tooltip as ReactTooltip } from 'react-tooltip'

interface CustomReactSelectProps extends Omit<ReactSelectProps, 'onChange'> {
    label?: string;
    required?: boolean;
    error?: FieldErrors;
    errorName?: string;
    onChange?: (value: any) => void;
    wrapperClassName?: string;
    disabled?: boolean;
    tooltip?: {
        id: string;
        content: string;
        place?: 'top' | 'bottom' | 'left' | 'right';
    };
}

// Custom Dropdown Indicator
const CustomDropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <img src={chevRght} alt="" aria-hidden="true" className="dropdown-icon" />
        </components.DropdownIndicator>
    );
};

const CustomReactSelect: React.FC<CustomReactSelectProps> = ({
    label,
    required = false,
    error,
    errorName,
    onChange,
    wrapperClassName = '',
    className = 'com-r-select',
    classNamePrefix = 'com-r-select',
    closeMenuOnScroll = true,
    closeMenuOnSelect = true,
    tooltip,
    disabled = false,
    ...rest
}) => {

    const handleChange = (selectedOption: any) => {
        if (disabled) return; // prevent change when disabled
        if (onChange) {
            onChange(selectedOption);
        }
    };

    const inputId = React.useId();
    const errorId = `${inputId}-error`;

    const hasError = Boolean(
        typeof error === 'string'
            ? error
            : error && errorName && (error as any)[errorName]
    );

    return (
        <div className={`com-input-grp ${wrapperClassName}`}>
            {label && (
                <label className="form-label" htmlFor={inputId}>
                    {label}
                    {required && <span className="label-star">*</span>}

                    {tooltip && (
                        <>
                            <button
                                type="button"
                                data-tooltip-id={tooltip.id}
                                className='text-decoration-none cursor-pointer ms-2 p-0 border-0 bg-transparent'
                                aria-label={`${label} information`}
                                style={{ display: 'inline', verticalAlign: 'middle' }}
                            >
                                <i className='icon-info me-2' style={{ fontSize: '12px' }}></i>
                            </button>

                            <ReactTooltip
                                style={{
                                    zIndex: '1',
                                    wordBreak: "break-word",
                                    maxWidth: "90%"
                                }}
                                id={tooltip.id}
                                place={tooltip.place || "bottom"}
                                content={tooltip.content}
                            />
                        </>
                    )}
                </label>
            )}

            <div className="com-input-grp">
                <div className="custom-select">
                    <ReactSelect
                        {...rest}
                        inputId={inputId}
                        aria-invalid={hasError}
                        aria-describedby={hasError ? errorId : undefined}
                        aria-errormessage={hasError ? errorId : undefined}
                        className={className}
                        classNamePrefix={classNamePrefix}
                        onChange={handleChange}
                        isDisabled={disabled}   // ⭐ important
                        closeMenuOnScroll={closeMenuOnScroll}
                        closeMenuOnSelect={closeMenuOnSelect}
                        components={{
                            DropdownIndicator: CustomDropdownIndicator,
                            ...rest.components,
                        }}
                    />
                </div>

                {error && errorName && (
                    <ErrorMessage
                        errors={error}
                        name={errorName}
                        render={({ message }) => (
                            <p id={errorId} className="valid-error" tabIndex={0} role="alert" aria-live="polite">
                                {message}
                            </p>
                        )}
                    />
                )}
            </div>
        </div>
    );
};


export default CustomReactSelect;